

















































































































import Component from "vue-class-component";
import { BaseRootPageComponent } from "@/components/BaseRootPageComponent";
import LoadingIndicator from "@/components/LoadingIndicator.vue";
import CoreAPI from "@/utils/core-api";
import * as modals from "@/utils/modals";
import { Device } from "@/models";
import ColumnHeader from "@/components/ColumnHeader.vue";
import { VNode } from "vue";

interface EditableDevice extends Device {
  isEditing: boolean;
  currentName?: string;
}

interface Header {
  text: string;
  field: string;
}

@Component({
  components: { LoadingIndicator, ColumnHeader }
})
export default class Devices extends BaseRootPageComponent {
  loading = true;
  search = "";
  devices: EditableDevice[] = [];
  sortColumn: keyof EditableDevice = "lastActivity";
  isSortAscending = false;

  headers: Header[] = [
    { text: "Name", field: "name" },
    { text: "Serial", field: "serial" },
    { text: "Patient ID", field: "patientID" },
    { text: "Last IP", field: "lastKnownIp" },
    { text: "Last Activity", field: "lastActivity" },
    { text: "Created At", field: "createdAt" },
    { text: "Updated At", field: "updatedAt" }
  ];

  sortByColumn(column: keyof EditableDevice) {
    if (column === this.sortColumn) {
      this.isSortAscending = !this.isSortAscending;
    } else {
      this.isSortAscending = true;
    }

    this.sortColumn = column;
    this.sortDevices();
  }

  sortDevices() {
    this.devices.sort((a: EditableDevice, b: EditableDevice) => {
      const modifier = this.isSortAscending ? 1 : -1;

      // If the value for a or b are undefined/null treat them as an empty string
      const valueA = (a[this.sortColumn] ?? "") as string;
      const valueB = (b[this.sortColumn] ?? "") as string;

      // When sorting ascending, sort null and empty values as last
      if (valueA.length > 0 && valueB.length === 0) return -1 * modifier;
      if (valueA.length === 0 && valueB.length > 0) return 1 * modifier;

      if (valueA < valueB) return -1 * modifier;
      if (valueA > valueB) return 1 * modifier;
      return 0;
    });
  }

  get displayedItems() {
    return this.devices.filter(c => c.serial.indexOf(this.search) !== -1 || c.name?.indexOf(this.search) !== -1);
  }

  async created() {
    this.setTitle("Devices");
    await this.fetchDevices();
  }

  async fetchDevices() {
    this.loading = true;

    try {
      const devices = await CoreAPI.getDevices();

      this.devices = devices.map(device => {
        // Add in the custom properties that are used on this page that aren't in the device object from the API so
        // that these fields will be reactive (the page will update when the value changes)
        // This is a Vue 2 issue that has been resolved in Vue 3
        const d = device as EditableDevice;

        d.currentName = undefined;
        d.isEditing = false;

        return d;
      });
      this.sortDevices();
    } catch (error) {
      this.devices = [];

      if ((error as any).isNetworkError) {
        await modals.showLostConnectionModal(this.$bvModal);
      } else {
        await modals.showUnexpectedErrorModal(this.$bvModal);
      }
    }

    this.loading = false;
  }

  editVentName(device: EditableDevice) {
    // Save the current name when editing starts so the user can cancel
    device.currentName = device.name;
    device.isEditing = true;
  }

  async updateDeviceName(device: EditableDevice) {
    const isValid = await this.validateDeviceName(device);
    if (!isValid) return;

    this.loading = true;
    try {
      await CoreAPI.updateDeviceName(device);
      device.isEditing = false;
    } catch (error) {
      if ((error as any).isNetworkError) {
        await modals.showLostConnectionModal(this.$bvModal);
      } else {
        await modals.showUnexpectedErrorModal(this.$bvModal);
      }
    } finally {
      this.loading = false;
    }
  }

  async validateDeviceName(device: EditableDevice): Promise<boolean> {
    const messages: string[] = [];

    if (!this.checkDeviceNameCharacters(device.name)) {
      messages.push("Vent name should only contain: letters, numbers, dashes (-) or underscores (_)");
    }
    if (!this.checkDeviceNameUnique(device)) {
      messages.push("Given vent name is already taken");
    }
    if (!this.checkDeviceNameLength(device.name)) {
      messages.push("Vent name length should be 25 characters or less");
    }

    if (messages.length > 0) {
      // Create an bullet list of the validation messages to make it easier for the user to read. To do this,
      // vue-bootstrap needs the HTML generated using Vue's createElement function
      const items = messages.map(m => this.$createElement("li", {}, m));
      const list = this.$createElement("ul", {}, items);

      await this.showOkModal("Invalid Device Name", [list]);
      return false;
    }

    return true;
  }

  showOkModal(title: string, message: string | VNode[]) {
    const t = message[0] as VNode;
    return this.$bvModal.msgBoxOk(message, { title: title });
  }

  cancelUpdateVentName(device: EditableDevice) {
    device.name = device.currentName;
    device.isEditing = false;
  }

  checkDeviceNameCharacters(name: string | undefined) {
    const regEx = RegExp("[^a-zA-Z0-9-_]");

    if (name) {
      return !regEx.test(name);
    }
    return true;
  }

  checkDeviceNameUnique(device: EditableDevice) {
    if (device && device.name) {
      return (
        this.devices.filter(d => d.serial !== device.serial && d.name?.toLowerCase() === device.name?.toLowerCase())
          .length === 0
      );
    }
    return true; //Returning true if the name is empty, this gives the user the ability to clear the name
  }

  checkDeviceNameLength(name: string | undefined) {
    // If the device name is empty, it means the user is trying to remove the name, which is allowed
    if (!name) return true;

    return name.length >= 1 && name.length <= 25;
  }
}
